// Terms.js
// Full Rosterable LLC Terms of Service. Public route, reached from the TopBar menu.
// Layout + typography come from the shared LegalDocument component.

import React from "react";
import LegalDocument, { H2, H3, P, Strong, Bullets, Mail, Link } from "../components/LegalDocument";

export default function Terms() {
  return (
    <LegalDocument title="Terms of Service" effectiveDate="April 12, 2026">
      <P>
        Please read these Terms of Service (“Terms”) carefully before using the Rosterable mobile
        application (the “App”). By downloading, installing, or using the App, you agree to be bound
        by these Terms. If you do not agree, do not use the App.
      </P>

      <H2>1. Acceptance of Terms</H2>
      <P>
        These Terms constitute a legally binding agreement between you and Rosterable LLC, a limited
        liability company (“Rosterable LLC,” “we,” “us,” or “our”). You must be at least 13 years old
        to create an account. By creating an account, you represent that you are at least 13 years of
        age. If you are under 18, you represent that your parent or guardian has reviewed and agreed
        to these Terms on your behalf.
      </P>

      <H2>2. Description of the App</H2>
      <P>Rosterable is a sports trivia and roster-building game application that allows users to:</P>
      <Bullets items={[
        "Compete in streak-based trivia games using historical player data",
        "Draft fantasy sports rosters across multiple sports",
        "Build and manage a Dynasty roster, complete Dynasty Challenges, and earn reward packs",
        "View leaderboards and compare scores with other users",
        "Track personal draft history, badges, XP, and statistics",
        "Save favorite teams and share draft results",
      ]} />
      <P>
        The App uses historical player data for entertainment purposes only. Rosterable LLC is not
        affiliated with, endorsed by, or connected to any professional sports league, team, players’
        association, or athlete.
      </P>
      <P>
        The App uses the following third-party services to operate: Google Firebase (authentication,
        database, and crash reporting) and Google AdMob (advertising). Your use of the App is subject
        to the terms and privacy policies of these providers.
      </P>

      <H2>3. User Accounts</H2>

      <H3>3.1 Account Creation</H3>
      <P>
        You may use the App as a guest without an account. To access features such as leaderboards,
        cross-device sync, badges, and subscription benefits, you must create an account by providing
        a username and email address. Passwords must be at least 8 characters and contain at least one
        letter and one number. You must be at least 13 years old to create an account.
      </P>

      <H3>3.2 Account Responsibilities</H3>
      <P>You are responsible for:</P>
      <Bullets items={[
        "Maintaining the confidentiality of your password",
        "All activity that occurs under your account",
        "Ensuring your account information is accurate and up to date",
        "Notifying us immediately of any unauthorized use of your account",
      ]} />
      <P>We reserve the right to suspend or terminate accounts that violate these Terms.</P>

      <H3>3.3 Username Policy</H3>
      <P>
        Usernames must be between 4 and 16 characters and may only contain letters, numbers, and
        underscores. Usernames must not contain profanity, hate speech, impersonation of real
        individuals, or any content that violates these Terms. We reserve the right to remove or
        modify usernames that violate this policy without notice.
      </P>

      <H2>4. Subscriptions and Purchases</H2>

      <H3>4.1 Available Plans</H3>
      <P>Rosterable LLC offers the following plans:</P>
      <Bullets items={[
        <><Strong>Free</Strong> — includes a limited number of drafts and streaks per sport each day. Free users may optionally watch rewarded video ads to unlock bonus plays.</>,
        <><Strong>PRO Monthly</Strong> — auto-renewing monthly subscription. Removes all ads, increases daily draft and streak limits, provides additional Dynasty Challenge attempts, and unlocks other premium features.</>,
        <><Strong>PRO Lifetime</Strong> — one-time non-consumable purchase. Grants all PRO Monthly benefits permanently with no recurring charge. This is not a subscription and does not auto-renew.</>,
      ]} />

      <H3>4.2 Billing and Auto-Renewal (PRO Monthly)</H3>
      <P>
        PRO Monthly subscriptions are billed through your Apple ID account via Apple’s In-App Purchase
        system. Subscriptions automatically renew at the end of each billing period unless cancelled at
        least 24 hours before the renewal date. Your Apple ID account will be charged for renewal within
        24 hours prior to the end of the current period.
      </P>

      <H3>4.3 PRO Lifetime Purchase</H3>
      <P>
        PRO Lifetime is a one-time, non-consumable in-app purchase processed through Apple’s In-App
        Purchase system. Once purchased, access is permanent and tied to your Apple ID. You may restore
        this purchase on any device signed into the same Apple ID via the “Restore Purchases” option in
        the App. PRO Lifetime does not auto-renew and you will never be charged again after the initial
        purchase.
      </P>
      <P>
        If you purchase PRO Lifetime while an active PRO Monthly subscription is in effect, both
        entitlements will coexist until your monthly subscription expires or is cancelled. We recommend
        cancelling your monthly subscription promptly to avoid future charges, as Apple does not permit
        apps to cancel subscriptions on your behalf.
      </P>

      <H3>4.4 Managing and Cancelling Subscriptions</H3>
      <P>
        You can manage and cancel your PRO Monthly subscription at any time through your Apple ID account
        settings (Settings &gt; [Your Name] &gt; Subscriptions). Cancellation takes effect at the end of
        the current billing period — you will retain access to premium features until that date. Refund
        eligibility for partial periods is determined by Apple. PRO Lifetime purchases are not subject to
        cancellation.
      </P>

      <H3>4.5 Price Changes</H3>
      <P>
        We reserve the right to change subscription prices. Apple will notify you of price changes before
        they take effect, and your continued subscription after the change constitutes acceptance of the
        new price. Existing PRO Lifetime purchasers are not affected by price changes.
      </P>

      <H3>4.6 Refunds</H3>
      <P>
        All purchases are processed by Apple. Refund requests must be submitted directly to Apple through{" "}
        <Link href="https://reportaproblem.apple.com">reportaproblem.apple.com</Link>. We do not control
        Apple’s refund decisions. If you initiate a chargeback or payment dispute for a valid in-app
        purchase, we reserve the right to suspend or terminate your account and revoke access to any
        associated benefits pending resolution of the dispute.
      </P>

      <H3>4.7 Dynasty Reward Packs</H3>
      <P>
        Dynasty Reward Packs are consumable in-app purchases processed through Apple’s In-App Purchase
        system. Consumable purchases are used upon opening and are permanently consumed. Because your
        Dynasty data is stored in your account, unopened packs are retained if you delete and reinstall
        the App, provided you log back into the same account. However, consumable purchases cannot be
        restored via the “Restore Purchases” function, and all sales of consumable Dynasty Reward Packs
        are final. Refund requests must be directed to Apple at{" "}
        <Link href="https://reportaproblem.apple.com">reportaproblem.apple.com</Link>.
      </P>

      <H3>4.8 Modification of Features and Content</H3>
      <P>
        We reserve the right to modify, adjust, or discontinue any App feature, subscription benefit, or
        in-app purchase offering at any time, with or without notice. This includes changes to game
        mechanics, reward pack contents, and PRO tier features. We are not liable for any perceived loss
        of value resulting from such changes.
      </P>

      <H2>5. Acceptable Use</H2>

      <H3>5.1 Permitted Use</H3>
      <P>
        You may use the App solely for your personal, non-commercial entertainment purposes in
        accordance with these Terms.
      </P>

      <H3>5.2 Prohibited Conduct</H3>
      <P>You agree not to:</P>
      <Bullets items={[
        "Use the App for any unlawful purpose or in violation of any applicable laws",
        "Attempt to gain unauthorized access to any part of the App or its related systems",
        "Use automated tools, bots, scripts, or other means to interact with the App",
        "Manipulate leaderboards, scores, or game mechanics through any unauthorized means",
        "Reverse engineer, decompile, or disassemble any portion of the App",
        "Scrape, harvest, or collect data from the App without our express written permission",
        "Harass, threaten, or harm other users",
        "Post or transmit any content that is defamatory, obscene, hateful, or otherwise objectionable",
        "Impersonate any person or entity or misrepresent your affiliation with any person or entity",
        "Interfere with or disrupt the integrity or performance of the App",
      ]} />

      <H2>6. Intellectual Property</H2>

      <H3>6.1 Our Content</H3>
      <P>
        The App and all of its content, features, and functionality — including but not limited to the
        software, design, graphics, text, logos, and game mechanics — are owned by Rosterable LLC and are
        protected by United States and international copyright, trademark, and other intellectual property
        laws.
      </P>

      <H3>6.2 Player Data</H3>
      <P>
        Historical player names, statistics, and career information used in the App are factual public
        information compiled for trivia and entertainment purposes. The use of factual information about
        public figures in a trivia context does not constitute an endorsement by or affiliation with any
        athlete, team, or league. Rosterable LLC does not claim ownership over any athlete’s name,
        likeness, or statistics. All player names, team names, and related marks belong to their
        respective owners.
      </P>

      <H3>6.3 Limited License</H3>
      <P>
        We grant you a limited, non-exclusive, non-transferable, revocable license to download and use the
        App on your personal iOS device solely for your personal, non-commercial entertainment. This
        license does not include the right to reproduce, modify, distribute, or create derivative works of
        any App content.
      </P>

      <H2>7. Leaderboards and User Content</H2>
      <P>
        By submitting scores to the leaderboard, you grant Rosterable LLC a non-exclusive, worldwide,
        royalty-free license to display your username and score within the App and in connection with
        promoting the App. Leaderboard standings are provided for entertainment purposes and confer no
        real-world value, prize, or entitlement.
      </P>
      <P>
        We reserve the right to remove any leaderboard entry or reset scores that we reasonably believe
        were achieved through unauthorized means.
      </P>

      <H2>8. Disclaimers</H2>
      <P>
        The App is provided on an “as is” and “as available” basis without warranties of any kind, either
        express or implied, including but not limited to warranties of merchantability, fitness for a
        particular purpose, non-infringement, or uninterrupted or error-free operation.
      </P>
      <P>
        We do not warrant that the App will be free from bugs, errors, or other defects, or that any
        defects will be corrected. We do not warrant the accuracy of any historical player data or
        statistics used in the App.
      </P>
      <P>
        Rosterable is a game of skill and entertainment and does not involve gambling, real-money
        wagering, or prizes of monetary value. Player names and statistics are used solely as the basis
        for trivia and skill-based gameplay.
      </P>

      <H2>9. Limitation of Liability</H2>
      <P>
        To the fullest extent permitted by applicable law, Rosterable LLC shall not be liable for any
        indirect, incidental, special, consequential, or punitive damages arising out of or related to
        your use of the App, even if we have been advised of the possibility of such damages.
      </P>
      <P>
        Our total liability to you for all claims arising out of or related to the App shall not exceed
        the total amount you paid to us in the twelve months preceding the claim.
      </P>

      <H2>10. Indemnification</H2>
      <P>
        You agree to indemnify, defend, and hold harmless Rosterable LLC and its officers, directors,
        employees, and agents from and against any claims, liabilities, damages, losses, and expenses
        (including reasonable legal fees) arising out of or in connection with your use of the App, your
        violation of these Terms, or your violation of any rights of another person or entity.
      </P>

      <H2>11. Termination</H2>
      <P>
        We reserve the right to suspend or terminate your access to the App at any time, with or without
        cause, and with or without notice. Upon termination, your license to use the App will immediately
        cease. The provisions relating to intellectual property, disclaimers, limitation of liability,
        indemnification, and arbitration will survive any termination.
      </P>
      <P>
        You may terminate your account at any time through the Settings screen in the App or by contacting
        us. Termination of your account does not entitle you to a refund of any subscription fees or
        consumable purchases.
      </P>

      <H2>12. Governing Law and Dispute Resolution</H2>
      <P>
        These Terms are governed by and construed in accordance with the laws of the State of California,
        United States, without regard to its conflict of law principles.
      </P>
      <P>
        Any dispute arising out of or relating to these Terms or the App shall first be attempted to be
        resolved through informal negotiation by contacting us at <Mail />. If the dispute cannot be
        resolved informally within 30 days, you agree that it shall be submitted to binding arbitration in
        California under the rules of the American Arbitration Association, except that either party may
        seek injunctive relief in a court of competent jurisdiction for intellectual property claims.
      </P>
      <P>
        You and Rosterable LLC agree that each may bring claims against the other only in your or its
        individual capacity and not as a plaintiff or class member in any purported class or
        representative proceeding.
      </P>

      <H2>13. Changes to These Terms</H2>
      <P>
        We reserve the right to modify these Terms at any time. When we make material changes, we will
        update the Effective Date and provide notice through the App or by email. Your continued use of
        the App after changes take effect constitutes your acceptance of the revised Terms.
      </P>

      <H2>14. Apple App Store Additional Terms</H2>
      <P>The following terms apply to your use of the App obtained through the Apple App Store:</P>
      <Bullets items={[
        "These Terms are between you and Rosterable LLC only, not Apple. Apple has no obligation to provide maintenance or support for the App.",
        "Apple is not responsible for the App or its content.",
        "In the event of any failure of the App to conform to any applicable warranty, you may notify Apple, and Apple will refund the purchase price (if any). To the maximum extent permitted by law, Apple has no other warranty obligation with respect to the App.",
        "Apple is not responsible for addressing any claims by you or any third party relating to the App, including product liability claims, consumer protection claims, or intellectual property infringement claims.",
        "If a third party claims the App infringes their intellectual property rights, Apple is not responsible for the investigation, defence, settlement, or discharge of such claim.",
        "You must comply with applicable third-party terms of service when using the App.",
        "Apple and Apple’s subsidiaries are third-party beneficiaries of these Terms and, upon your acceptance, Apple will have the right to enforce these Terms against you as a third-party beneficiary.",
      ]} />

      <H2>15. Miscellaneous</H2>
      <P>
        These Terms, together with our Privacy Policy, constitute the entire agreement between you and
        Rosterable LLC regarding your use of the App. If any provision of these Terms is found to be
        unenforceable, the remaining provisions will continue in full force and effect. Our failure to
        enforce any right or provision of these Terms will not be considered a waiver of those rights.
      </P>

      <H2>16. Contact Us</H2>
      <P>If you have any questions about these Terms, please contact us:</P>
      <P>
        Rosterable LLC<br />
        <Mail />
      </P>
    </LegalDocument>
  );
}
